import { parseAsString, useQueryState } from "nuqs"
import { useEffect, useRef } from "react"
import { QUERY_PARAM_KEYS } from "./keys"
import { type QueryParamKey, useClearFilters } from "./use-clear-filters"
import { useStatusQueryParam } from "./use-status-query-param"

export function useSearchQueryQueryParam() {
  const [searchQuery, setSearchQuery] = useQueryState(
    QUERY_PARAM_KEYS.searchQuery,
    parseAsString.withOptions({ history: "push" }),
  )

  // Get current status to check for 'hidden'
  const { statuses, onSelectStatus } = useStatusQueryParam()
  const hasHiddenStatus = statuses.includes("hidden")

  // To prevent infinite loops when updating
  const isUpdatingRef = useRef(false)

  // Clear all filters except searchQuery
  const clearFiltersExceptSearchQuery = useClearFilters({
    excludeKeys: [QUERY_PARAM_KEYS.searchQuery] as QueryParamKey[],
  })

  // Clear only the search query
  const clearOnlySearchQuery = useClearFilters({
    excludeKeys: Object.keys(QUERY_PARAM_KEYS).filter(
      key => key !== "searchQuery",
    ) as QueryParamKey[],
  })

  // Custom set function that clears other filters when setting a search query
  async function setSearchQueryWithFilters(value: string | null) {
    // If we're already in the middle of an update, wait a bit
    if (isUpdatingRef.current) {
      await new Promise(resolve => {
        setTimeout(resolve, 50)
      })
    }

    isUpdatingRef.current = true

    try {
      // If we're setting a non-empty search query
      if (value) {
        // Check if we have other filters active
        const currentUrl = new URL(window.location.href)
        const params = currentUrl.searchParams

        // Get current state to preserve "hidden" if needed
        const shouldKeepHidden = hasHiddenStatus

        // If there are other filters active, we'll need to do a coordinated update
        const hasOtherFilters = Array.from(params.keys()).some(param => {
          if (param === QUERY_PARAM_KEYS.searchQuery) {
            return false
          }
          if (param === QUERY_PARAM_KEYS.status) {
            const statusValues = params.get(param)?.split(",") || []
            return statusValues.some(statusValue => statusValue !== "hidden")
          }
          return true
        })

        if (hasOtherFilters) {
          // For a smoother experience, we'll perform both operations in a single batch
          // First, create a new URLSearchParams that only has our searchQuery
          const newParams = new URLSearchParams()
          newParams.set(QUERY_PARAM_KEYS.searchQuery, value)

          // Add hidden status if needed
          if (shouldKeepHidden) {
            newParams.set(QUERY_PARAM_KEYS.status, "hidden")
          }

          // Update URL directly using history API to avoid flickering
          const newUrl = new URL(window.location.href)
          newUrl.search = newParams.toString()

          // Use history.replaceState to update URL without causing a navigation
          window.history.replaceState({}, "", newUrl.toString())

          // Now update React state to match the URL
          // This avoids the brief flash of both states
          await setSearchQuery(value)

          // If we need to set hidden, do it after a short delay
          if (shouldKeepHidden) {
            setTimeout(async () => {
              await onSelectStatus("all")
              await onSelectStatus("hidden")
            }, 10)
          }
        } else {
          // No other filters, just set query and clear everything else
          await setSearchQuery(value)

          // Don't need to clear if there are no other filters
          if (
            Array.from(params.keys()).length > 1 ||
            (params.has(QUERY_PARAM_KEYS.status) && !shouldKeepHidden)
          ) {
            clearFiltersExceptSearchQuery()

            // Restore hidden if needed
            if (shouldKeepHidden) {
              setTimeout(async () => {
                await onSelectStatus("all")
                await onSelectStatus("hidden")
              }, 10)
            }
          }
        }
      } else {
        // For empty values, just set directly
        await setSearchQuery(value)
      }
    } catch (error) {
      console.error("Error setting search query:", error)
      // In case of error, make sure to reset the updating flag
      isUpdatingRef.current = false
      throw error
    } finally {
      // Ensure we reset the flag after a reasonable delay
      setTimeout(() => {
        isUpdatingRef.current = false
      }, 100)
    }
  }

  // Effect to detect other filters being added and clear search query if needed
  useEffect(() => {
    // Nothing to do if there's no search query or we're updating
    if (!searchQuery || isUpdatingRef.current) {
      return
    }

    // This is a "one-time" check that runs whenever searchQuery changes
    const checkOnce = () => {
      const currentUrl = new URL(window.location.href)
      const params = currentUrl.searchParams

      // Check if we have any params other than searchQuery and hidden status
      const hasOtherFilters = Array.from(params.keys()).some(param => {
        // Skip search query
        if (param === QUERY_PARAM_KEYS.searchQuery) {
          return false
        }

        // For status, check if it contains any values other than "hidden"
        if (param === QUERY_PARAM_KEYS.status) {
          const statusValues = params.get(param)?.split(",") || []
          return statusValues.some(value => value !== "hidden")
        }

        // Any other param means we have other filters
        return true
      })

      // If other filters exist with search query, clear the search query
      if (hasOtherFilters && searchQuery) {
        isUpdatingRef.current = true
        clearOnlySearchQuery()
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 50)
      }
    }

    // Run once after a short delay to ensure URL is updated
    const timerId = setTimeout(checkOnce, 10)

    return () => {
      clearTimeout(timerId)
    }
  }, [searchQuery, clearOnlySearchQuery])

  // Listen for URL changes to detect filter changes
  useEffect(() => {
    // Skip if we're updating
    if (isUpdatingRef.current || !searchQuery) {
      return
    }

    const handleUrlChange = () => {
      if (isUpdatingRef.current) {
        return
      }

      const currentUrl = new URL(window.location.href)
      const params = currentUrl.searchParams

      // Check if we have any params other than searchQuery and hidden status
      const hasOtherFilters = Array.from(params.keys()).some(param => {
        // Skip search query
        if (param === QUERY_PARAM_KEYS.searchQuery) {
          return false
        }

        // For status, check if it contains any values other than "hidden"
        if (param === QUERY_PARAM_KEYS.status) {
          const statusValues = params.get(param)?.split(",") || []
          return statusValues.some(value => value !== "hidden")
        }

        // Any other param means we have other filters
        return true
      })

      if (hasOtherFilters && searchQuery) {
        isUpdatingRef.current = true
        clearOnlySearchQuery()
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 50)
      }
    }

    window.addEventListener("popstate", handleUrlChange)

    return () => {
      window.removeEventListener("popstate", handleUrlChange)
    }
  }, [searchQuery, clearOnlySearchQuery])

  return {
    searchQuery,
    setSearchQuery: setSearchQueryWithFilters,
  }
}
